import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Anchor, Button, message, BackTop, Drawer, Space } from 'antd';
import { 
  CopyOutlined, 
  MenuOutlined, 
  FullscreenOutlined,
  FullscreenExitOutlined,
  PrinterOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import '../styles/markdown.css';

interface MarkdownViewerProps {
  content: string;
  fileName?: string;
  showToc?: boolean;
  className?: string;
  style?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
}

interface TocItem {
  id: string;
  text: string;
  level: number;
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  content,
  fileName = 'document.md',
  showToc = true,
  className = '',
  style = {},
  containerStyle = {}
}) => {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeAnchor, setActiveAnchor] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tocDrawerVisible, setTocDrawerVisible] = useState(false);

  // 生成目录
  useEffect(() => {
    const headings: TocItem[] = [];
    const lines = content.split('\n');
    
    lines.forEach((line, index) => {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = `heading-${index}-${text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
        headings.push({ id, text, level });
      }
    });
    
    setTocItems(headings);
  }, [content]);

  // 处理代码复制
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('代码已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 处理全屏
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 处理打印
  const handlePrint = () => {
    window.print();
  };

  // 处理下载
  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success('文档已下载');
  };

  // 渲染目录
  const renderToc = () => {
    if (tocItems.length === 0) return null;

    return (
      <div className="markdown-toc">
        <div className="markdown-toc-title">目录</div>
        <Anchor
          affix={false}
          onClick={(e, link) => {
            e.preventDefault();
            const element = document.querySelector(link.href);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
              setActiveAnchor(link.href);
            }
          }}
        >
          {tocItems.map((item) => (
            <Anchor.Link
              key={item.id}
              href={`#${item.id}`}
              title={
                <span
                  style={{
                    paddingLeft: `${(item.level - 1) * 12}px`,
                    fontSize: item.level === 1 ? '14px' : item.level === 2 ? '13px' : '12px',
                    fontWeight: item.level === 1 ? 600 : 'normal'
                  }}
                >
                  {item.text}
                </span>
              }
            />
          ))}
        </Anchor>
      </div>
    );
  };

  // Markdown组件配置
  const markdownComponents = useMemo(() => ({
    h1: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h1 id={id} {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h3 id={id} {...props}>{children}</h3>;
    },
    h4: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h4 id={id} {...props}>{children}</h4>;
    },
    h5: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h5 id={id} {...props}>{children}</h5>;
    },
    h6: ({ children, ...props }: any) => {
      const index = tocItems.findIndex(item => item.text === String(children));
      const id = tocItems[index]?.id || '';
      return <h6 id={id} {...props}>{children}</h6>;
    },
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const codeString = String(children).replace(/\n$/, '');
      
      if (!inline && language) {
        return (
          <div style={{ position: 'relative', margin: '16px 0' }}>
            <div style={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              display: 'flex',
              gap: 8
            }}>
              <span style={{
                padding: '2px 8px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                fontSize: 12,
                color: '#888',
                textTransform: 'uppercase'
              }}>
                {language}
              </span>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyCode(codeString)}
                style={{ opacity: 0.8 }}
              >
                复制
              </Button>
            </div>
            <SyntaxHighlighter
              style={vscDarkPlus}
              language={language}
              PreTag="div"
              showLineNumbers
              {...props}
            >
              {codeString}
            </SyntaxHighlighter>
          </div>
        );
      }
      
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    table({ children }: any) {
      return (
        <div style={{ overflowX: 'auto', margin: '16px 0' }}>
          <table className="markdown-table">{children}</table>
        </div>
      );
    },
    blockquote({ children }: any) {
      return (
        <blockquote className="markdown-blockquote">
          {children}
        </blockquote>
      );
    },
    a({ href, children, ...props }: any) {
      // 处理内部锚点链接
      if (href?.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const element = document.querySelector(href);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            {...props}
          >
            {children}
          </a>
        );
      }
      // 外部链接在新窗口打开
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    }
  }), [tocItems]);

  return (
    <div 
      className={`markdown-viewer-container ${className} ${isFullscreen ? 'fullscreen' : ''}`}
      style={{
        ...containerStyle,
        ...(isFullscreen ? {} : { minHeight: 'auto', height: 'auto' })
      }}
    >
      {/* 工具栏 */}
      <div className="markdown-toolbar" style={{
        padding: '8px 16px',
        borderBottom: '1px solid #e8e8e8',
        background: '#fafafa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Space>
          {showToc && tocItems.length > 0 && (
            <Button
              icon={<MenuOutlined />}
              onClick={() => setTocDrawerVisible(true)}
              className="toc-toggle-mobile"
            >
              目录
            </Button>
          )}
        </Space>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            title="下载Markdown文件"
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            title="打印文档"
          />
          <Button
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
            title={isFullscreen ? '退出全屏' : '全屏显示'}
          />
        </Space>
      </div>

      {/* 主体内容 */}
      <Row gutter={24} style={{ 
        height: isFullscreen ? 'calc(100% - 50px)' : 'auto',
        minHeight: isFullscreen ? 'calc(100% - 50px)' : 'auto'
      }}>
        {/* 桌面端目录 */}
        {showToc && tocItems.length > 0 && (
          <Col xs={0} sm={0} md={6} lg={5} xl={4} style={{ 
            height: isFullscreen ? '100%' : 'auto',
            maxHeight: isFullscreen ? '100%' : 'calc(100vh - 300px)',
            overflow: 'auto',
            position: isFullscreen ? 'relative' : 'sticky',
            top: isFullscreen ? 0 : 20
          }}>
            {renderToc()}
          </Col>
        )}
        
        {/* 文档内容 */}
        <Col 
          xs={24} 
          sm={24} 
          md={showToc && tocItems.length > 0 ? 18 : 24} 
          lg={showToc && tocItems.length > 0 ? 19 : 24} 
          xl={showToc && tocItems.length > 0 ? 20 : 24}
          style={{ 
            height: isFullscreen ? '100%' : 'auto',
            overflow: isFullscreen ? 'auto' : 'visible'
          }}
        >
          <div className="markdown-viewer" style={{ padding: '24px', ...style }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>
          </div>
        </Col>
      </Row>

      {/* 移动端目录抽屉 */}
      <Drawer
        title="目录"
        placement="left"
        onClose={() => setTocDrawerVisible(false)}
        visible={tocDrawerVisible}
        width={280}
      >
        {renderToc()}
      </Drawer>

      {/* 回到顶部 */}
      <BackTop />

      {/* 全屏样式 */}
      <style dangerouslySetInnerHTML={{ __html: `
        .markdown-viewer-container.fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999;
          background: white;
        }
        
        .toc-toggle-mobile {
          display: none;
        }
        
        @media (max-width: 768px) {
          .toc-toggle-mobile {
            display: inline-block;
          }
        }
        
        @media print {
          .markdown-toolbar,
          .markdown-toc,
          .ant-back-top {
            display: none !important;
          }
          
          .markdown-viewer {
            padding: 0 !important;
          }
        }
      `}} />
    </div>
  );
};

export default MarkdownViewer;